import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

const AVATAR_COLORS = [
    "#4A90D9", "#70A098", "#c5a3fb", "#ffa7d4",
    "#82d9e3", "#a8e21d", "#e9605d", "#f0a030",
    "#7b68ee", "#20b2aa", "#cd853f", "#6b8e9b",
];

const getAvatarColor = (name) => {
    if (!name) return AVATAR_COLORS[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const WorkspaceAvatar = ({ logoUrl, name, size }) => {
    const sizeVal = size || 24;
    const fontSize = Math.round(sizeVal * 0.5);
    const initial = name ? name.trim().charAt(0).toUpperCase() : "?";
    const bgColor = getAvatarColor(name);

    return (
        <span
            className="workspace-switcher__item-avatar"
            style={{ width: sizeVal, height: sizeVal, fontSize: fontSize, background: logoUrl ? "transparent" : bgColor }}
        >
            {logoUrl ? (
                <img src={logoUrl} alt={name || "Workspace"} />
            ) : (
                initial
            )}
        </span>
    );
};

WorkspaceAvatar.propTypes = {
    logoUrl: PropTypes.string,
    name: PropTypes.string,
    size: PropTypes.number,
};

const WorkspaceSwitcherItem = ({ label, isActive, onClick, logoUrl, isDefault, defaultLabel }) => {
    return (
        <button
            type="button"
            className={
                "workspace-switcher__item" +
                (isActive ? " workspace-switcher__item--active" : "")
            }
            onClick={onClick}
            title={label}
        >
            <WorkspaceAvatar logoUrl={logoUrl} name={label} size={24} />
            <span className="workspace-switcher__item-label">{label}</span>
            {isDefault && <span className="workspace-switcher__default-badge">{defaultLabel}</span>}
            {isActive && <i className="workspace-switcher__checkmark pe-7s-check" aria-hidden="true" />}
        </button>
    );
};

WorkspaceSwitcherItem.propTypes = {
    label: PropTypes.string.isRequired,
    isActive: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
    logoUrl: PropTypes.string,
    isDefault: PropTypes.bool,
    defaultLabel: PropTypes.string,
};

const WorkspaceSwitcher = ({ workspaces, activeWorkspaceId, onSwitch, onNewWorkspace, myProjectsLabel, newWorkspaceLabel }) => {
    const resolvedNewWorkspaceLabel = newWorkspaceLabel || "New Workspace";
    const defaultLabel = "Default";
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState(null);
    const [pendingWorkspace, setPendingWorkspace] = useState(null);
    const containerRef = useRef(null);
    const latestSwitchRequestRef = useRef(0);
    const isDefaultPersonalWorkspace = (workspace) => (
        workspace &&
        (workspace.isDefaultPersonalWorkspace === true || workspace.isDefaultWorkspace === true) &&
        workspace.myRole === "owner"
    );
    const defaultPersonalWorkspace = (workspaces || []).find(isDefaultPersonalWorkspace);
    const personalWorkspaceId = defaultPersonalWorkspace ? defaultPersonalWorkspace.workspaceId : null;
    const personalWorkspaceLabel = defaultPersonalWorkspace && defaultPersonalWorkspace.workspaceName
        ? defaultPersonalWorkspace.workspaceName
        : myProjectsLabel;
    const visibleWorkspaces = defaultPersonalWorkspace
        ? (workspaces || []).filter((workspace) => workspace.workspaceId !== defaultPersonalWorkspace.workspaceId)
        : (workspaces || []);
    const normalizeWorkspaceId = (workspaceId) => workspaceId || null;
    const getWorkspaceSummary = (workspaceId) => {
        if (defaultPersonalWorkspace && (!workspaceId || workspaceId === defaultPersonalWorkspace.workspaceId)) {
            return {
                workspaceId: personalWorkspaceId,
                label: personalWorkspaceLabel,
                logoUrl: defaultPersonalWorkspace.logoUrl || null,
            };
        }
        if (!workspaceId) {
            return { workspaceId: null, label: myProjectsLabel, logoUrl: null };
        }
        const workspace = visibleWorkspaces.find((w) => w.workspaceId === workspaceId);
        return workspace
            ? { workspaceId: workspace.workspaceId, label: workspace.workspaceName, logoUrl: workspace.logoUrl || null }
            : null;
    };
    const updateDropdownPosition = useCallback(() => {
        if (!containerRef.current) {
            return;
        }

        const triggerRect = containerRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
        const sideMargin = 12;
        const preferredWidth = 360;
        const availableWidth = viewportWidth - (sideMargin * 2);
        const width = Math.min(availableWidth, Math.max(triggerRect.width, Math.min(preferredWidth, availableWidth)));
        const maxLeft = Math.max(sideMargin, viewportWidth - width - sideMargin);
        const left = Math.min(Math.max(sideMargin, triggerRect.left), maxLeft);

        setDropdownStyle({
            position: "fixed",
            top: `${triggerRect.bottom + 4}px`,
            left: `${left}px`,
            width: `${width}px`,
        });
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setDropdownStyle(null);
            return undefined;
        }

        updateDropdownPosition();
        window.addEventListener("resize", updateDropdownPosition);
        window.addEventListener("scroll", updateDropdownPosition, true);

        return () => {
            window.removeEventListener("resize", updateDropdownPosition);
            window.removeEventListener("scroll", updateDropdownPosition, true);
        };
    }, [isOpen, updateDropdownPosition]);

    // Determine the active workspace to display when collapsed
    const getActiveWorkspace = () => {
        return getWorkspaceSummary(activeWorkspaceId) || { workspaceId: null, label: myProjectsLabel, logoUrl: null };
    };

    const activeWs = pendingWorkspace || getActiveWorkspace();
    const isSwitching = !!pendingWorkspace;

    useEffect(() => {
        if (pendingWorkspace && normalizeWorkspaceId(activeWorkspaceId) === normalizeWorkspaceId(pendingWorkspace.workspaceId)) {
            setPendingWorkspace(null);
        }
    }, [activeWorkspaceId, pendingWorkspace]);

    useEffect(() => {
        if (typeof document === "undefined" || !document.body) {
            return undefined;
        }
        document.body.classList.toggle("workspace-switching", isSwitching);
        return () => {
            document.body.classList.remove("workspace-switching");
        };
    }, [isSwitching]);

    const handleToggle = () => {
        if (isSwitching) {
            return;
        }
        const nextIsOpen = !isOpen;
        if (nextIsOpen) {
            updateDropdownPosition();
        }
        setIsOpen(nextIsOpen);
    };

    const handleSelect = (workspaceId) => {
        setIsOpen(false);
        const normalizedWorkspaceId = normalizeWorkspaceId(workspaceId);
        const normalizedActiveWorkspaceId = normalizeWorkspaceId(activeWorkspaceId);
        const normalizedPersonalWorkspaceId = normalizeWorkspaceId(personalWorkspaceId);
        const isSelectingDefaultPersonalWorkspace = normalizedPersonalWorkspaceId && normalizedWorkspaceId === normalizedPersonalWorkspaceId;
        const isDefaultPersonalWorkspaceActive = isSelectingDefaultPersonalWorkspace && (
            normalizedActiveWorkspaceId === null ||
            normalizedActiveWorkspaceId === normalizedPersonalWorkspaceId
        );
        if (normalizedWorkspaceId === normalizedActiveWorkspaceId || isDefaultPersonalWorkspaceActive) {
            return;
        }
        const selectedWorkspace = getWorkspaceSummary(workspaceId);
        if (selectedWorkspace) {
            setPendingWorkspace(selectedWorkspace);
        }
        if (onSwitch) {
            const switchRequestId = latestSwitchRequestRef.current + 1;
            latestSwitchRequestRef.current = switchRequestId;
            let switchPromise;
            try {
                switchPromise = onSwitch(workspaceId);
            } catch (error) {
                switchPromise = Promise.reject(error);
            }
            Promise.resolve(switchPromise).then(
                function() {},
                function() {}
            ).then(function() {
                if (latestSwitchRequestRef.current === switchRequestId) {
                    setPendingWorkspace(null);
                }
            });
        }
    };

    return (
        <div
            ref={containerRef}
            className={"workspace-switcher" + (isOpen ? " is-open" : "")}
        >
            <button
                type="button"
                className={"workspace-switcher__trigger" + (isSwitching ? " workspace-switcher__trigger--switching" : "")}
                onClick={handleToggle}
                aria-expanded={isOpen}
                aria-haspopup="menu"
                aria-busy={isSwitching}
                disabled={isSwitching}
            >
                <WorkspaceAvatar logoUrl={activeWs.logoUrl} name={activeWs.label} size={24} />
                <span className="workspace-switcher__trigger-label">{activeWs.label}</span>
                {isSwitching ? (
                    <span className="workspace-switcher__switching-spinner" aria-hidden="true" />
                ) : (
                    <i className="workspace-switcher__chevron pe-7s-angle-down" aria-hidden="true" />
                )}
            </button>
            <div className="workspace-switcher__dropdown" role="menu" style={dropdownStyle || undefined}>
                <WorkspaceSwitcherItem
                    label={personalWorkspaceLabel}
                    logoUrl={defaultPersonalWorkspace ? defaultPersonalWorkspace.logoUrl : null}
                    isActive={defaultPersonalWorkspace ? (!activeWorkspaceId || activeWorkspaceId === defaultPersonalWorkspace.workspaceId) : !activeWorkspaceId}
                    isDefault={!!defaultPersonalWorkspace}
                    defaultLabel={defaultLabel}
                    onClick={() => handleSelect(personalWorkspaceId)}
                />
                {visibleWorkspaces.map((workspace) => (
                    <WorkspaceSwitcherItem
                        key={workspace.workspaceId}
                        label={workspace.workspaceName}
                        logoUrl={workspace.logoUrl}
                        isActive={activeWorkspaceId === workspace.workspaceId}
                        isDefault={workspace.isDefaultWorkspace === true}
                        defaultLabel={defaultLabel}
                        onClick={() => handleSelect(workspace.workspaceId)}
                    />
                ))}
                {onNewWorkspace && (
                    <>
                        <div className="workspace-switcher__separator" />
                        <button
                            type="button"
                            className="workspace-switcher__item workspace-switcher__new-workspace"
                            onClick={() => { setIsOpen(false); onNewWorkspace(); }}
                        >
                            <i className="workspace-switcher__new-icon pe-7s-plus" aria-hidden="true" />
                            <span className="workspace-switcher__item-label">{resolvedNewWorkspaceLabel}</span>
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

WorkspaceSwitcher.propTypes = {
    workspaces: PropTypes.arrayOf(
        PropTypes.shape({
            workspaceId: PropTypes.string.isRequired,
            workspaceName: PropTypes.string.isRequired,
            myRole: PropTypes.string,
            isAdmin: PropTypes.bool,
            isDefaultPersonalWorkspace: PropTypes.bool,
            isDefaultWorkspace: PropTypes.bool,
            logoUrl: PropTypes.string,
        })
    ).isRequired,
    activeWorkspaceId: PropTypes.string,
    onSwitch: PropTypes.func.isRequired,
    onNewWorkspace: PropTypes.func,
    myProjectsLabel: PropTypes.string.isRequired,
    newWorkspaceLabel: PropTypes.string,
};

export default WorkspaceSwitcher;
